/**
 * Resolves a numeral.js format string for a single Portfolio Analysis cell
 * based on (format alias, measureKind, user preferences).
 *
 * Why per-cell instead of overriding the platform FormattingContext: a
 * FormattingProvider override would leak into every descendant component
 * that calls `useFormattingContext()`, which includes things outside PA
 * (sub-rendered DA components, tooltips, etc.). Computing the format
 * string per cell scopes the customization to PA only.
 *
 * Mapping:
 *   - currency: $-prefix, decimals.currency, parens or minus on negatives
 *   - return:   %-suffix, decimals.return,   parens or minus on negatives
 *   - weight:   %-suffix, decimals.weight,   parens or minus on negatives
 *   - ratio:    'integer' formats stay integer-ish; everything else uses
 *               numeral's `x` multiplier suffix with decimals.ratio
 *   - anything else (date, title, etc.): pass through unchanged
 */

function decimalSuffix (decimals) {
  const n = Number.isFinite(decimals) ? Math.max(0, Math.floor(decimals)) : 0
  return n === 0 ? '' : `.${'0'.repeat(n)}`
}

function buildCurrency (decimals, showSymbol, negParens) {
  const body = `0,0${decimalSuffix(decimals)}`
  // numeral.js: parens form is `$(0,0.00)` (note: parens wrap the WHOLE pattern
  // including the `$`); minus form is `$0,0.00` and numeral renders negatives
  // with a leading minus by default.
  if (negParens) return showSymbol ? `$(${body})` : `(${body})`
  return showSymbol ? `$${body}` : body
}

function buildPercent (decimals, negParens) {
  const body = `0,0${decimalSuffix(decimals)}`
  // numeral.js percent: `(0,0.00)%` for parens-on-negatives, `0,0.00%` for plain.
  return negParens ? `(${body})%` : `${body}%`
}

function buildMultiplier (decimals) {
  // numeral.js: format strings ending in 'x' are special-cased by FormattingContext
  // (see createFormatter -> rawFormatString slice). 'x' suffix renders e.g. '1.5x'.
  return `0,0${decimalSuffix(decimals)}x`
}

function buildInteger (negParens) {
  // window:rank etc. are intentionally integer; never decimals.
  return negParens ? '(0,0)' : '0,0'
}

export function effectiveFormat (formatAlias, measureKind, prefs) {
  if (!formatAlias) return formatAlias
  if (!prefs?.display?.decimals) return formatAlias

  const decimals = prefs.display.decimals
  const negParens = prefs.display.negativeFormat !== 'minus'
  const showSym = prefs.display.showCurrencySymbol !== false

  if (measureKind === 'currency') return buildCurrency(decimals.currency ?? 2, showSym, negParens)
  if (measureKind === 'return') return buildPercent(decimals.return ?? 2, negParens)
  if (measureKind === 'weight') return buildPercent(decimals.weight ?? 2, negParens)
  if (measureKind === 'ratio') {
    if (formatAlias === 'integer') return buildInteger(negParens)
    return buildMultiplier(decimals.ratio ?? 2)
  }
  // Any non-numeric measureKind (or undefined) passes through unchanged.
  // This is the catch-all for date/title/bool/etc. format aliases -- we
  // don't maintain an explicit allowlist because it's a maintenance hazard
  // (a new platform format alias would silently miss it).
  return formatAlias
}
